<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useSmallScreen } from 'dashboard/composables/useSmallScreen';
import { settingsCenterTarget } from './settingsContext';
import {
  isCenterPlace,
  settingsBackTarget,
  settingsBarVisible,
} from './settingsBack';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { isSmallScreen } = useSmallScreen();

const visible = computed(() => settingsBarVisible(route));
const fromCenter = computed(() => route.query.config_center === '1');
const back = computed(() =>
  visible.value ? settingsBackTarget(router, route) : null
);
const backLabel = computed(() => {
  if (!back.value?.labelKey) return t('SETTINGS_CENTER.BACK');
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t('SETTINGS_CENTER.BACK_TO', { place: t(back.value.labelKey) });
});
const center = computed(() => settingsCenterTarget(route));
// "Todas as configurações" é secundário e só aparece quando o menu lateral das
// configurações não está na tela (entrada pela central ou celular) e quando o
// voltar já não leva para a própria central.
const showAllSettings = computed(
  () =>
    !isCenterPlace(back.value?.labelKey) &&
    (fromCenter.value || isSmallScreen.value)
);
const scope = computed(() => {
  const value = route.query.config_scope;
  return ['account', 'personal', 'funnel', 'inbox'].includes(value)
    ? value.toUpperCase()
    : 'ACCOUNT';
});

const goBack = () => {
  if (!back.value) return;
  if (back.value.mode === 'history') router.back();
  else router.push(back.value.to);
};
</script>

<template>
  <div
    v-if="visible"
    data-testid="settings-context-bar"
    class="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-n-weak bg-n-solid-1 px-4 py-2 text-sm"
  >
    <button
      v-if="back"
      type="button"
      data-testid="settings-back"
      class="inline-flex min-h-9 items-center gap-2 rounded-lg px-2 -mx-2 font-semibold text-n-blue-11 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
      @click="goBack"
    >
      <span class="i-ph-arrow-left size-4 rtl:rotate-180" aria-hidden="true" />
      {{ backLabel }}
    </button>
    <template v-if="fromCenter">
      <span
        class="rounded-full border border-n-weak px-3 py-1 text-xs text-n-slate-11"
      >
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
        {{ t(`SETTINGS_CENTER.SCOPES.${scope}`) }}
      </span>
      <span class="hidden flex-1 text-xs text-n-slate-11 lg:block">
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
        {{ t(`SETTINGS_CENTER.SCOPE_HELP.${scope}`) }}
      </span>
    </template>
    <RouterLink
      v-if="showAllSettings"
      :to="center"
      data-testid="settings-all"
      class="ml-auto inline-flex min-h-9 items-center text-xs text-n-slate-11 hover:text-n-slate-12 hover:underline"
    >
      {{ t('SETTINGS_CENTER.ALL_SETTINGS') }}
    </RouterLink>
  </div>
</template>
